import { format, formatDistanceToNow, parseISO, isValid } from "date-fns";
import { ptBR } from "date-fns/locale";

export const PAGE_SIZE = 5;
/** Tamanho do lote ao buscar todos os fretes no servidor (paginação por cursor). */
export const FETCH_BATCH_SIZE = 20;
const MAX_FETCH_BATCHES = 100;

function unwrapDetail(response) {
  const root = response?.data;
  if (root?.data != null && typeof root.data === "object" && !Array.isArray(root.data)) {
    return root.data;
  }
  if (Array.isArray(root?.data) && root.data.length === 1) {
    return root.data[0];
  }
  if (root && typeof root === "object" && !Array.isArray(root) && root.id) {
    return root;
  }
  return root?.data ?? root;
}

function formatLocation(city, uf, fallback = "") {
  if (city && uf) return `${city}/${uf}`;
  return city || uf || fallback || "—";
}

function formatLocationFromPlace(place, stringFallback = "") {
  if (!place) return stringFallback || "—";
  if (typeof place === "string") return place;
  if (place.label) return place.label;
  return formatLocation(place.city, place.uf || place.state, stringFallback);
}

function parseNumber(value) {
  if (value == null || value === "") return null;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  const parsed = Number(String(value).replace(/\./g, "").replace(",", "."));
  return Number.isFinite(parsed) ? parsed : null;
}

function normalizeContractType(item) {
  const raw =
    item.contractingType ??
    item.bidding?.contractingType ??
    item.contractType ??
    item.contract_type ??
    item.hiringType ??
    item.typeContract ??
    item.modality ??
    "";
  const text = String(raw).toLowerCase();
  if (text.includes("fixo") || text.includes("fixed") || text.includes("preco")) return "Preço Fixo";
  if (text.includes("bid") || text.includes("lance")) return "BID";
  if (item.type === "fixed" || item.isFixedPrice === true) return "Preço Fixo";
  if (item.type === "bid" || item.isBid === true) return "BID";
  return raw || "Preço Fixo";
}

function resolveFreightType(contractType) {
  const text = String(contractType).toLowerCase();
  if (text.includes("bid") || text.includes("lance")) return "bid";
  return "fixed";
}

export function isBidFreight(freight) {
  return freight?.type === "bid";
}

function formatPublishedAgo(item) {
  if (item.publishedAgo) return item.publishedAgo;
  const dateRaw =
    item.publishedAt ??
    item.published_at ??
    item.createdAt ??
    item.created_at ??
    item.releaseDate;
  if (!dateRaw) return "";
  try {
    const date = typeof dateRaw === "string" ? parseISO(dateRaw) : new Date(dateRaw);
    if (!isValid(date)) return "";
    return `Publicado ${formatDistanceToNow(date, { addSuffix: true, locale: ptBR })}`;
  } catch {
    return "";
  }
}

function parseCount(value) {
  if (value == null || value === "") return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
}

function formatFreightNumber(item) {
  if (item.numberSerial != null && item.numberSerial !== "") {
    return String(item.numberSerial);
  }

  const code = item.freightCode ?? item.number ?? item.code ?? item.serial ?? item.freightNumber;
  if (code == null || code === "") return "—";

  return String(code).replace(/^FT-/i, "");
}

function extractOrigin(item) {
  if (item.origin && typeof item.origin === "string") return item.origin;
  return formatLocationFromPlace(
    item.origin ?? item.firstDelivery,
    item.origem,
  );
}

function extractDestination(item) {
  if (item.destination && typeof item.destination === "string") return item.destination;
  return formatLocationFromPlace(
    item.destination ?? item.lastDelivery,
    item.destino,
  );
}

function extractVehicleType(item) {
  return (
    item.vehicleType ??
    item.vehicle_type ??
    item.typeVehicle ??
    selectedVehicleNames(item.vehicle?.typeVehicle) ??
    "—"
  );
}

function selectedVehicleNames(typeVehicle) {
  const items = Array.isArray(typeVehicle?.dados) ? typeVehicle.dados : [];
  const names = items.filter((i) => i?.selected).map((i) => i?.name).filter(Boolean);
  return names.length ? names.join(", ") : null;
}

function extractBodyType(item) {
  return (
    item.bodyworkType ??
    item.bodyType ??
    item.body_type ??
    item.carroceria ??
    selectedVehicleNames(item.vehicle?.typeBodyWork) ??
    "—"
  );
}

function extractCollectionDate(item) {
  return (
    item.collectionDate ??
    item.dateColeta ??
    item.firstDelivery?.dateColeta ??
    item.origin?.dateColeta ??
    ""
  );
}

function extractDeliveryDate(item) {
  return (
    item.deliveryDate ??
    item.dateEntrega ??
    item.lastDelivery?.dateEntrega ??
    item.lastDelivery?.dateColeta ??
    ""
  );
}

function formatWeight(item) {
  const raw = item.weightCargo ?? item.weight ?? item.peso ?? item.weightKg;
  if (raw == null || raw === "") return "—";
  const num = Number(raw);
  if (!Number.isFinite(num)) return String(raw);
  return `${num.toLocaleString("pt-BR")} kg`;
}

function formatBidTime(value) {
  if (!value) return "—";
  if (typeof value === "string" && /^\d{2}:\d{2}$/.test(value)) return value;
  try {
    const date = typeof value === "string" ? parseISO(value) : new Date(value);
    if (!isValid(date)) return String(value);
    return format(date, "HH:mm");
  } catch {
    return String(value);
  }
}

function asNonEmptyString(value) {
  if (value == null || value === "") return "";
  const str = String(value).trim();
  return str || "";
}

function extractShipperId(item) {
  if (!item || typeof item !== "object") return "";

  const clientPayment =
    item.clientPayment ?? item.client_payment ?? item.client ?? {};

  const pagadorRaw = item.pagador ?? item.freight?.pagador;
  const pagadorUid =
    typeof pagadorRaw === "string"
      ? pagadorRaw
      : pagadorRaw?.uid ?? pagadorRaw?.id ?? "";

  const shipperObj = item.shipper;
  const shipperUid =
    shipperObj && typeof shipperObj === "object"
      ? shipperObj.uid ?? shipperObj.id ?? ""
      : "";

  return (
    asNonEmptyString(item.idShipper) ||
    asNonEmptyString(clientPayment?.uid) ||
    asNonEmptyString(clientPayment?.id) ||
    asNonEmptyString(pagadorUid) ||
    asNonEmptyString(item.shipperUid) ||
    asNonEmptyString(item.uidShipperPayment) ||
    asNonEmptyString(item.shipperId) ||
    asNonEmptyString(item.idShipperPayment) ||
    asNonEmptyString(shipperUid) ||
    ""
  );
}

function extractCarrierQueueStatus(item) {
  if (!item || typeof item !== "object") {
    return { isInCarrierQueue: false, joinedAt: null };
  }

  const carrierQueue =
    item.carrierQueue && typeof item.carrierQueue === "object"
      ? item.carrierQueue
      : item.transportQueue && typeof item.transportQueue === "object"
        ? item.transportQueue
        : {};

  const rawFlag =
    item.isInCarrierQueue ??
    item.inCarrierQueue ??
    carrierQueue.inQueue ??
    carrierQueue.isInQueue ??
    false;

  const isInCarrierQueue =
    rawFlag === true ||
    rawFlag === 1 ||
    String(rawFlag).toLowerCase() === "true" ||
    String(rawFlag).toUpperCase() === "IN_QUEUE";

  const joinedAt =
    carrierQueue.dataHora ??
    carrierQueue.joinedAt ??
    carrierQueue.registeredAt ??
    item.carrierQueueJoinedAt ??
    null;

  return {
    isInCarrierQueue,
    joinedAt,
    carrierQueue: isInCarrierQueue ? carrierQueue : null,
  };
}

function extractShipper(item) {
  return (
    item.clientPaymentName ??
    item.shipperName ??
    item.shipper ??
    item.clientPayment?.name ??
    item.client?.name ??
    item.embarcador ??
    "—"
  );
}

function extractObservations(item) {
  if (Array.isArray(item.observations)) return item.observations.filter(Boolean);
  if (Array.isArray(item.observacoes)) return item.observacoes.filter(Boolean);
  if (typeof item.observations === "string" && item.observations.trim()) {
    return [item.observations.trim()];
  }
  if (typeof item.observation === "string" && item.observation.trim()) {
    return [item.observation.trim()];
  }
  return [];
}

function mapBidHistoryItem(bid, index) {
  return {
    position: bid.position ?? bid.rank ?? bid.posicao ?? index + 1,
    carrier:
      bid.carrierName ??
      bid.carrier ??
      bid.transportadora ??
      bid.companyName ??
      "—",
    amount: parseNumber(bid.amount ?? bid.value ?? bid.lance ?? bid.bidValue) ?? 0,
    time: formatBidTime(bid.submittedAt ?? bid.time ?? bid.hour ?? bid.horario ?? bid.createdAt),
  };
}

function extractBidHistory(item) {
  const history =
    item.bidding?.bidHistory ??
    item.bidHistory ??
    item.bidsHistory ??
    item.bids ??
    item.lances ??
    item.topBids ??
    [];
  if (!Array.isArray(history)) return [];
  return history.slice(0, 5).map(mapBidHistoryItem);
}

function mapBaseFields(item) {
  const id = item.id ?? item._id ?? item.freightId;
  const bidding = item.bidding ?? {};
  const dispute = bidding.dispute ?? {};
  const yourBid = bidding.yourBid ?? {};
  const contractType = normalizeContractType(item);
  const type = resolveFreightType(contractType);

  const bestBid = parseNumber(
    item.bestBidAmount ??
      dispute.bestBidAmount ??
      item.bestBid ??
      item.best_bid ??
      item.currentBestBid ??
      item.lowestBid,
  );

  const freightValue = parseNumber(
    item.freightValue ??
      item.fixedPriceAmount ??
      item.values?.valueFreightage ??
      item.freight_value ??
      item.valueFreightage ??
      item.fixedPrice ??
      item.price,
  );

  const participants =
    item.participantsCount ??
    dispute.participantsCount ??
    item.participants ??
    item.bidCount ??
    0;

  const userPosition =
    yourBid.position ??
    item.userPosition ??
    item.userRank ??
    item.myPosition ??
    null;

  const carrierQueueStatus = extractCarrierQueueStatus(item);

  return {
    id,
    number: formatFreightNumber(item),
    type,
    origin: extractOrigin(item),
    destination: extractDestination(item),
    vehicleType: extractVehicleType(item),
    bodyType: extractBodyType(item),
    collectionDate: extractCollectionDate(item),
    contractType,
    bestBid: bestBid ?? freightValue ?? 0,
    freightValue: freightValue ?? bestBid ?? 0,
    participants,
    publishedAgo: formatPublishedAgo(item),
    shipper: extractShipper(item),
    idShipper: extractShipperId(item),
    weight: formatWeight(item),
    volume: item.volume ?? item.cubagem ?? (item.volumeM3 ? `${item.volumeM3} m³` : "—"),
    deliveryDate: extractDeliveryDate(item),
    modality: item.contractingLabel ?? bidding.contractingLabel ?? item.modality ?? contractType,
    observations: extractObservations(item),
    bidHistory: extractBidHistory(item),
    userPosition,
    yourBidAmount: parseNumber(yourBid.amount),
    canSubmitBid: yourBid.canSubmit !== false,
    isInCarrierQueue: carrierQueueStatus.isInCarrierQueue,
    carrierQueueJoinedAt: carrierQueueStatus.joinedAt,
    carrierQueue: carrierQueueStatus.carrierQueue,
  };
}

export function mapAvailableListItem(item) {
  const mapped = mapBaseFields(item);
  return {
    id: mapped.id,
    number: mapped.number,
    type: mapped.type,
    contractingLabel: item.contractingLabel ?? mapped.modality,
    origin: mapped.origin,
    destination: mapped.destination,
    vehicleType: mapped.vehicleType,
    bodyType: mapped.bodyType,
    collectionDate: mapped.collectionDate,
    contractType: mapped.contractType,
    bestBid: mapped.bestBid,
    freightValue: mapped.freightValue,
    participants: mapped.participants,
    publishedAgo: mapped.publishedAgo,
    shipper: mapped.shipper,
    idShipper: mapped.idShipper,
    isInCarrierQueue: mapped.isInCarrierQueue,
    carrierQueueJoinedAt: mapped.carrierQueueJoinedAt,
    carrierQueue: mapped.carrierQueue,
  };
}

export function mapAvailableDetail(item) {
  return mapBaseFields(item);
}

export function buildAvailableQueryParams(
  filters,
  { limit = PAGE_SIZE, cursor, idCarrier } = {},
) {
  const params = { limit };

  if (cursor) params.cursor = cursor;
  if (idCarrier) params.idCarrier = idCarrier;

  const numberFilter = filters.number?.trim();
  if (numberFilter) {
    const normalized = numberFilter.replace(/^FT-/i, "").trim();
    if (/^\d+$/.test(normalized)) {
      params.numberSerial = normalized;
    } else {
      params.freightCode = numberFilter;
    }
  }

  if (filters.origin?.trim()) params.origem = filters.origin.trim();
  if (filters.destination?.trim()) params.destino = filters.destination.trim();
  if (filters.vehicleType) params.vehicleType = filters.vehicleType;
  if (filters.bodyType) params.bodyworkType = filters.bodyType;
  if (filters.collectionDate) params.collectionDate = filters.collectionDate;

  params.contractingType = "FIXED";

  return params;
}

export function parseAvailableListResponse(response, limit, cursorForPage) {
  const root = response?.data ?? {};
  const items = Array.isArray(root.data)
    ? root.data
    : Array.isArray(root?.items)
      ? root.items
      : [];

  const pagination = root.pagination ?? {};
  const nextCursor = pagination.nextCursor ?? pagination.next_cursor ?? null;
  const serverHasMore = pagination.hasMore === true || pagination.has_more === true;
  const total = parseCount(root.count) ??
    parseCount(pagination.total) ??
    parseCount(pagination.totalCount) ??
    parseCount(pagination.count);

  const hasValidNextCursor =
    typeof nextCursor === "string" &&
    nextCursor.trim().length > 0 &&
    nextCursor !== cursorForPage;
  const hasMore = serverHasMore && hasValidNextCursor;

  return {
    items,
    nextCursor,
    hasMore,
    serverHasMore,
    total,
  };
}

export function getApiErrorMessage(error, fallback = "Ocorreu um erro inesperado.") {
  return (
    error?.response?.data?.message ??
    error?.response?.data?.msg ??
    error?.response?.data?.error ??
    error?.message ??
    fallback
  );
}

export function getShipperDisplayName(shipper, fallback = "") {
  const fantasyName = String(shipper?.dataPersonal?.nameFantasy || "").trim();
  const socialName = String(shipper?.dataPersonal?.socialName || "").trim();
  return fantasyName || socialName || fallback;
}

export function buildEnterQueuePayload(user, shipper) {
  const idCarrier = user?.uidShipper;
  if (!idCarrier) {
    throw new Error("Usuário sem transportadora vinculada.");
  }

  const nameCarrier = getShipperDisplayName(shipper, user?.name || "");
  if (!nameCarrier) {
    throw new Error("Nome da transportadora não informado.");
  }

  const idUser = user?.uid || "";
  if (!idUser) {
    throw new Error("Usuário não identificado.");
  }

  return {
    idCarrier,
    nameCarrier,
    idUser,
  };
}

export function buildLeaveQueuePayload(user) {
  const idCarrier = user?.uidShipper;
  if (!idCarrier) {
    throw new Error("Usuário sem transportadora vinculada.");
  }

  const idUser = user?.uid || "";
  if (!idUser) {
    throw new Error("Usuário não identificado.");
  }

  return { idCarrier, idUser };
}

export function formatCarrierQueueWaitingTime(freight) {
  const joinedAt =
    freight?.carrierQueueJoinedAt ??
    freight?.carrierQueue?.dataHora ??
    freight?.carrierQueue?.joinedAt ??
    freight?.carrierQueue?.registeredAt ??
    null;

  if (!joinedAt) return null;

  try {
    const date =
      typeof joinedAt === "string"
        ? parseISO(joinedAt)
        : joinedAt instanceof Date
          ? joinedAt
          : new Date(joinedAt);

    if (!isValid(date)) return null;

    return formatDistanceToNow(date, { locale: ptBR });
  } catch {
    return null;
  }
}

export function buildAcceptingClientPayment(user, shipper) {
  const uid = user?.uidShipper;
  if (!uid) {
    throw new Error("Usuário sem embarcador vinculado.");
  }

  return {
    uid,
    name: getShipperDisplayName(shipper, user?.name || ""),
    cnpj: shipper?.dataPersonal?.documentNumber || "",
  };
}

export function unwrapAvailableDetail(response) {
  return unwrapDetail(response);
}

/** Busca todos os fretes disponíveis percorrendo a paginação por cursor do backend. */
export async function fetchAllAvailableFreights(
  getAvailableFreights,
  filters,
  { idCarrier } = {},
) {
  const byId = new Map();
  let cursor;
  let totalFromApi = null;

  for (let batch = 0; batch < MAX_FETCH_BATCHES; batch += 1) {
    const params = buildAvailableQueryParams(filters, {
      limit: FETCH_BATCH_SIZE,
      cursor,
      idCarrier,
    });

    const response = await getAvailableFreights(params);
    const parsed = parseAvailableListResponse(response, FETCH_BATCH_SIZE, cursor);

    if (parsed.total != null) totalFromApi = parsed.total;

    parsed.items.forEach((item) => {
      const id = item.id ?? item._id;
      if (id) byId.set(id, item);
    });

    if (!parsed.serverHasMore || !parsed.nextCursor) break;
    if (parsed.nextCursor === cursor) break;
    cursor = parsed.nextCursor;
  }

  return {
    items: Array.from(byId.values()),
    total: totalFromApi ?? byId.size,
  };
}
